import { animate, style, transition, trigger } from '@angular/animations';

import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, Input, OnDestroy, OnInit, inject, output } from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { SwalComponent, SwalDirective } from '@sweetalert2/ngx-sweetalert2';
import { GetMatchModelExt } from 'core/entities';
import { Session } from 'core/session.service';
import { SignalRService } from 'core/signalr.service';
import { EMPTY } from 'core/utils';
import { GameMode, MatchEmojiType, MatchTeam } from 'models/enums';
import { GetMatchPlayerModel, GetMatchSetModel } from 'models/matches';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { LuxonPipe } from '../pipes/luxon.pipe';
import { CommentComponent } from './comment/comment.component';
import { MatchBoxAchievementsComponent } from './match-box-achievements/match-box-achievements.component';
import { MatchBoxGoalsComponent } from './match-box-goals/match-box-goals.component';
import { MatchBoxHeaderOneAgainstOneComponent } from './match-box-header-one-against-one/match-box-header-one-against-one.component';
import { MatchBoxHeaderTwoAgainstTwoComponent } from './match-box-header-two-against-two/match-box-header-two-against-two.component';
import { UpsertCommentComponent } from './upsert-comment/upsert-comment.component';

const shameAudio = new Audio('/assets/audio/shame.mp3');

@Component({
    selector: 'app-match-box',
    templateUrl: './match-box.component.html',
    styleUrls: ['./match-box.component.scss'],
    animations: [
        trigger('toggleAnimation', [
            transition(':enter', [style({ opacity: 0, height: 0 }), animate('300ms ease-in', style({ opacity: 1, height: '*' }))]),
            transition(':leave', [style({ opacity: 1, height: '*' }), animate('300ms ease-in', style({ opacity: 0, height: 0 }))]),
        ]),
        trigger('fasterToggleAnimation', [
            transition(':enter', [style({ opacity: 0, height: 0 }), animate('200ms ease-in', style({ opacity: 1, height: '*' }))]),
            transition(':leave', [style({ opacity: 1, height: '*' }), animate('200ms ease-in', style({ opacity: 0, height: 0 }))]),
        ]),
    ],
    imports: [
        MatchBoxHeaderOneAgainstOneComponent,
        MatchBoxHeaderTwoAgainstTwoComponent,
        MatIcon,
        MatBadge,
        MatTooltip,
        MatchBoxGoalsComponent,
        MatchBoxAchievementsComponent,
        CommentComponent,
        UpsertCommentComponent,
        TranslateModule,
        LuxonPipe,
        MatMenuModule,
        MatIconModule,
        SwalComponent,
        SwalDirective,
    ],
})
export class MatchBoxComponent implements OnInit, OnDestroy {
    private readonly http = inject(HttpClient);
    private readonly signalR = inject(SignalRService);
    private readonly session = inject(Session);
    private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);

    #unsubscribe = new Subject<void>();

    public matchEmojiType = MatchEmojiType;

    public selectedSet = -1;
    public gameMode = GameMode;

    public expA = 0;
    public expB = 0;
    public showComments = false;
    public showAchievements = false;
    public hoveredEmoji: MatchEmojiType = null;
    public emojies: Record<string, string> = {};

    public idPlayer: number;
    private _match: GetMatchModelExt;
    @Input() public set match(value: GetMatchModelExt) {
        this._match = value;
        if (value) {
            this.expA = value.players.find((p) => p.team === MatchTeam.A).experiencePoints;
            this.expB = value.players.find((p) => p.team === MatchTeam.B).experiencePoints;
        }
    }

    public get match() {
        return this._match;
    }

    public matchDeleted = output<GetMatchModelExt>();
    public matchEdit = output<GetMatchModelExt>();

    constructor() {
        const session = this.session;
        this.idPlayer = session.user.idPlayer;
    }

    ngOnInit() {
        this.signalR.emojiChanged$
            .pipe(
                takeUntil(this.#unsubscribe),
                filter((x) => this.match && x.idMatch === this.match.id),
            )
            .subscribe(({ data }) => {
                this.match.emojies = data;
            });

        this.signalR.matchCommentsChanged$
            .pipe(
                takeUntil(this.#unsubscribe),
                filter((x) => this.match && x.idMatch === this.match.id),
            )
            .subscribe(({ count }) => {
                this.match.commentsCount = count;

                this.loadComments();
            });

        this.signalR.achievementsChanged$
            .pipe(
                takeUntil(this.#unsubscribe),
                filter((x) => this.match && x.idMatch === this.match.id),
            )
            .subscribe(({ achievements }) => {
                this.match.achievements = achievements;
            });
    }

    ngOnDestroy() {
        this.#unsubscribe.next();
        this.#unsubscribe.complete();
    }

    public canEdit() {
        if (!this.match) {
            return false;
        }
        return (
            this.session.user.idPlayer === this.match.idPlayerCreated ||
            this.match.players.some((p) => p.idPlayer === this.session.user.idPlayer) ||
            this.session.user.isInRole('System administrator')
        );
    }

    public isShameMatch(match: GetMatchModelExt) {
        return match.sets.some((x) => x.scoreTeamA === 0 || x.scoreTeamB === 0);
    }

    public getRoleIcon(set: GetMatchSetModel, player: GetMatchPlayerModel) {
        if (set.idPlayerTeamADefense === player.idPlayer || set.idPlayerTeamBDefense === player.idPlayer) {
            return 'fa-shield-alt';
        } else {
            return 'fa-futbol';
        }
    }

    matchClick(match: GetMatchModelExt) {
        if (!this.isShameMatch(match)) {
            return;
        }
        shameAudio.play().catch(EMPTY);
    }

    public getEmojiCount(type: MatchEmojiType) {
        return this.match.emojies.filter((x) => x.type === type).length;
    }

    public onEmojiClick(event: MouseEvent, type: MatchEmojiType) {
        event.stopPropagation();
        this.http
            .post<{ values: Array<any> }>(`/api/matches/${this.match.id}/emojies`, {
                type: type,
                createdAt: new Date().toISOString(),
            })
            .subscribe((res) => {
                this.match.emojies = res.values;
            });
    }

    public onCommentsClick() {
        this.showComments = !this.showComments;
        this.showAchievements = false;
        if (!this.showComments) {
            this.selectedSet = -1;
        }
    }

    public onAchievementsClick() {
        this.showComments = false;
        this.selectedSet = -1;
        this.showAchievements = !this.showAchievements;
    }

    public loadComments() {
        this.http.get<any>(`/api/matches/${this.match.id}/comments`).subscribe((res) => {
            this.match.comments = res.values;
        });
    }

    public getEmojiPlayers(type: MatchEmojiType) {
        const res = this.match.emojies
            .filter((x) => x.type === type)
            .map((x) => x.playerDisplayName)
            .join(', ');

        return res;
    }

    public isEmojiActive(type: MatchEmojiType) {
        return this.match.emojies.some((x) => x.type === type && x.idPlayer === this.session.user.idPlayer);
    }

    public scrollIntoView() {
        this.element.nativeElement.scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => {
            this.showComments = true;
        }, 100);
    }

    public selectSet(ix: number) {
        this.selectedSet = this.selectedSet === ix ? -1 : ix;
        if (this.selectedSet >= 0) {
            this.showComments = false;
            this.showAchievements = false;
        }
    }

    public deleteMatch() {
        this.matchDeleted.emit(this.match);
    }

    public editMatch() {
        this.matchEdit.emit(this.match);
    }
}
